<div class="card" *ngIf="getLoadingState() == 2">
    <p-message *ngIf="minLicenceMessage" severity="warn" styleClass="mb-8 mt-6">
        {{ "user_licenseFreelancerWarning" | translate }}
    </p-message>

    <!-- p-table component adapted to use allGroups data -->
    <p-table
        data-testid="team-groups-table"
        #dt1
        [value]="allGroups"
        [globalFilterFields]="['name', 'members.length', 'tiles.length']"
        dataKey="id"
        selectionMode="single"
        [(selection)]="selectedGroup"
        (onRowSelect)="onRowSelect($event)"
        responsiveLayout="scroll"
        sortMode="multiple"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50, 100]"
        [showCurrentPageReport]="true"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
    >
        <ng-template pTemplate="caption">
            <div class="flex justify-between gap-2">
                <div class="flex items-center gap-2">
                    <p-iconfield>
                        <p-inputicon>
                            <i class="pi pi-search"></i>
                        </p-inputicon>
                        <input
                            data-testid="team-group-search"
                            pInputText
                            type="text"
                            [(ngModel)]="search"
                            placeholder="{{ 'user_groups_search' | translate }}"
                            class="w-full"
                        />
                    </p-iconfield>
                </div>

                <div class="flex items-center gap-2">
                    @if (currentUser?.hasPermissionStr("USER_CREATE")) {
                        <button
                            data-testid="team-group-create"
                            pButton
                            type="button"
                            pTooltip="{{ 'upgrade_to_pro_for_group_creation' | translate }}"
                            [tooltipDisabled]="isPro"
                            tooltipPosition="left"
                            [disabled]="!isPro"
                            (click)="createGroup()"
                            class="icon-button-fix"
                        >
                            <i class="pi pi-user-plus"></i> {{ "create_user_group" | translate }}
                        </button>
                    }
                </div>
            </div>
        </ng-template>

        <!-- Adapted header to reflect group attributes -->
        <ng-template pTemplate="header">
            <tr>
                <th pSortableColumn="name">
                    {{ "group_name_label" | translate }}
                    <p-sortIcon field="name"></p-sortIcon>
                </th>
                <th pSortableColumn="members.length">
                    {{ "group_members_label" | translate }}
                    <p-sortIcon field="members.length"></p-sortIcon>
                </th>
            </tr>
        </ng-template>

        <!-- Adapted body to correctly display data from allGroups -->
        <ng-template pTemplate="body" let-group>
            <tr
                [pSelectableRow]="group"
                [attr.data-testid]="'team-group-row-' + group.id"
                pTooltip="{{ 'upgrade_to_pro_for_groups' | translate }}"
                [tooltipDisabled]="isPro"
                tooltipPosition="top"
            >
                <td>{{ group.name }}</td>
                <td>
                    <ng-container *ngIf="group.members && group.members.length > 0; else noMembers"> {{ formatMemberNames(group.members) }} </ng-container>
                    <ng-template #noMembers>{{ "no_members_in_group" | translate }}</ng-template>
                </td>
            </tr>
        </ng-template>
    </p-table>
</div>

<div *ngIf="getLoadingState() == 1">
    <div class="mb-16 flex justify-center">
        <div class="mt-16">
            <div class="text-center items-center">
                <div class="mt-1">
                    <img class="inline" src="{{ 'new_group_image_src' | translate }}" width="65%" height="40%" alt="No data" />
                </div>
                <div class="mb-12">
                    <h4>{{ "new_group_template_header" | translate }}</h4>
                    <p style="max-width: 400px; margin: auto" [innerHTML]="'new_group_template_description' | translate"></p>
                </div>

                <div class="mb-2">
                    <p-button
                        data-testid="team-group-create"
                        severity="primary"
                        pRipple
                        type="button"
                        styleClass="p-button-xl"
                        pTooltip="{{ 'upgrade_to_pro_for_group_creation' | translate }}"
                        [tooltipDisabled]="isPro"
                        tooltipPosition="left"
                        [disabled]="!isPro"
                        (click)="createGroup()"
                        label="{{ 'create_new_group_label' | translate }}"
                        icon="pi pi-plus"
                    >
                    </p-button>
                </div>
            </div>
        </div>
    </div>
</div>

<p-dialog
    data-testid="team-group-edit-dialog"
    header="{{ 'edit_group_label' | translate }}"
    [(visible)]="showEditDialog"
    [modal]="true"
    [draggable]="true"
    [resizable]="false"
    [style]="{ width: '550px' }"
>
    <div class="p-fluid">
        <!-- Display Group Name (Non-editable) -->
        <div class="w-full mb-6">
            <label class="block mb-2" for="groupName">{{ "group_name_label" | translate }}</label>
            <input
                data-testid="team-group-edit-name"
                type="text"
                class="w-full"
                placeholder="{{ 'enter_group_name' | translate }}"
                pInputText
                [(ngModel)]="groupName"
            />
        </div>

        <!-- Dropdown for Group Members -->
        <div class="w-full mb-6">
            <label class="block mb-2" for="groupMembers">{{ "group_members_label" | translate }}</label>
            <p-multiSelect
                data-testid="team-group-edit-members"
                [options]="memberOptions"
                [(ngModel)]="selectedMembers"
                optionLabel="label"
                placeholder="{{ 'no_member_selection' | translate }}"
                selectedItemsLabel="{0} {{ 'standard_items_selected' | translate }}"
                class="w-full"
                appendTo="body"
            >
            </p-multiSelect>
        </div>
    </div>

    <ng-template pTemplate="footer">
        <div class="flex justify-between w-full">
            <div>
                <p-button
                    data-testid="team-group-delete"
                    severity="danger"
                    (click)="confirmDeletion($event)"
                    icon="pi pi-trash"
                    label="{{ 'button_delete' | translate }}"
                    styleClass="p-button-text"
                    [disabled]="isLoading"
                />
            </div>
            <div class="flex gap-2">
                <button
                    data-testid="team-group-edit-cancel"
                    type="submit"
                    pButton
                    (click)="onCancel()"
                    label="{{ 'button_cancel' | translate }}"
                    class="p-button-text mr-2"
                    severity="secondary"
                    [disabled]="isLoading"
                ></button>
                <p-button
                    data-testid="team-group-edit-save"
                    [loading]="isLoading"
                    [disabled]="isSaveDisabled()"
                    (click)="saveGroupChanges()"
                    icon="pi pi-save"
                    label="{{ 'button_save' | translate }}"
                />
            </div>
        </div>
    </ng-template>
</p-dialog>

<p-dialog
    data-testid="team-group-create-dialog"
    header="{{ 'create_new_group_label' | translate }}"
    [(visible)]="showNewGroupDialog"
    [modal]="true"
    [draggable]="true"
    [resizable]="false"
    [style]="{ width: '550px' }"
    (onHide)="onNewGrDialogHide()"
    (onShow)="onNewGrDialogShow()"
>
    <div class="p-fluid">
        <!-- group name -->
        <div class="w-full mb-6">
            <label class="block mb-2" for="groupName">{{ "group_name_label" | translate }}</label>
            <input
                data-testid="team-group-create-name"
                type="text"
                class="w-full"
                placeholder="{{ 'enter_group_name' | translate }}"
                pInputText
                [(ngModel)]="newGroupName"
                autofocus
            />
        </div>

        <!-- Dropdown for Group Members -->
        <div class="w-full mb-6">
            <label class="block mb-2" for="groupMembers">{{ "group_members_label" | translate }}</label>
            <p-multiSelect
                data-testid="team-group-create-members"
                [options]="memberOptions"
                [(ngModel)]="newGroupMembers"
                optionLabel="label"
                placeholder="{{ 'no_member_selection' | translate }}"
                selectedItemsLabel="{0} {{ 'standard_items_selected' | translate }}"
                class="w-full"
                appendTo="body"
            >
            </p-multiSelect>
        </div>
    </div>

    <ng-template pTemplate="footer">
        <div class="flex justify-between w-full">
            <div></div>
            <div>
                <button
                    data-testid="team-group-create-cancel"
                    type="submit"
                    pButton
                    (click)="onNewGrCancel()"
                    class="p-button-text mr-2"
                    severity="secondary"
                    [disabled]="isLoading"
                >
                    {{ "button_cancel" | translate }}
                </button>
                <p-button
                    data-testid="team-group-create-save"
                    [loading]="isLoading"
                    [disabled]="isGroupCreDisabled()"
                    (click)="createNewGroup()"
                    icon="pi pi-save"
                    label="{{ 'button_save' | translate }}"
                ></p-button>
            </div>
        </div>
    </ng-template>
</p-dialog>
